import React from 'react';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import styles from './category6.module.scss';
import COButton from 'shared/components/COButton';
import Category6Layout from './category6Layout';
import { UseFieldArrayAppend, UseFieldArrayRemove } from 'react-hook-form';
import {
  BusinessTravelData,
  DistanceBasedTravel,
  SpendBasedTravel,
  HotelStay,
} from 'models/ActivityDataEntryScopes/businessTravel.model';

interface PanelComponentProps {
  distanceFields: Record<'id', string>[];
  appendDistance: UseFieldArrayAppend<BusinessTravelData, 'distanceBasedTravel'>;
  removeDistance: UseFieldArrayRemove;
  spendFields: Record<'id', string>[];
  appendSpend: UseFieldArrayAppend<BusinessTravelData, 'spendBasedTravel'>;
  removeSpend: UseFieldArrayRemove;
  hotelFields: Record<'id', string>[];
  appendHotel: UseFieldArrayAppend<BusinessTravelData, 'hotelStays'>;
  removeHotel: UseFieldArrayRemove;
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string | string[]>>;
}

export const Category6PanelComponent = (props: PanelComponentProps) => {
  const {
    distanceFields,
    appendDistance,
    removeDistance,
    spendFields,
    appendSpend,
    removeSpend,
    hotelFields,
    appendHotel,
    removeHotel,
    setActiveTabKey,
  } = props;

  const handleAddDistance = (event?: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    const newEntry = new DistanceBasedTravel();
    // Set default values to ensure consistent rendering
    newEntry.numberOfPassengers = 1;
    newEntry.roundTrip = false;
    // Don't set travel mode - let user select it, but this ensures other fields are initialized
    appendDistance(newEntry, { shouldFocus: false });
    setActiveTabKey?.('1');
  };

  const handleAddSpend = (event?: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    appendSpend(new SpendBasedTravel(), { shouldFocus: false });
    setActiveTabKey?.('2');
  };

  const handleAddHotel = (event?: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    appendHotel(new HotelStay(), { shouldFocus: false });
    setActiveTabKey?.('3');
  };

  return [
    {
      key: '1',
      header: (
        <div>
          <div>Distance-Based Travel</div>
          <div className={styles.addAction} onClick={handleAddDistance}>
            <COButton text>
              <AddIcon />
              Add Travel
            </COButton>
          </div>
        </div>
      ),
      content: (
        <div className={styles.accordionContainer}>
          <div className={styles.accordionContentContainer}>
            {distanceFields.length === 0 ? (
              <div className={styles.noEntriesMessage}>
                No distance-based travel entries. Click "Add Travel" to add one.
              </div>
            ) : (
              distanceFields.map((field, index) => {
                return (
                  <div key={field.id}>
                    <Category6Layout
                      index={index}
                      type="distance"
                      remove={removeDistance}
                      setActiveTabKey={setActiveTabKey}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      ),
    },
    {
      key: '2',
      header: (
        <div>
          <div>Spend-Based Travel</div>
          <div className={styles.addAction} onClick={handleAddSpend}>
            <COButton text>
              <AddIcon />
              Add Expense
            </COButton>
          </div>
        </div>
      ),
      content: (
        <div className={styles.accordionContainer}>
          <div className={styles.accordionContentContainer}>
            {spendFields.length === 0 ? (
              <div className={styles.noEntriesMessage}>
                No spend-based travel entries. Click "Add Expense" to add one.
              </div>
            ) : (
              spendFields.map((field, index) => {
                return (
                  <div key={field.id}>
                    <Category6Layout
                      index={index}
                      type="spend"
                      remove={removeSpend}
                      setActiveTabKey={setActiveTabKey}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      ),
    },
    {
      key: '3',
      header: (
        <div>
          <div>Hotel Stays</div>
          <div className={styles.addAction} onClick={handleAddHotel}>
            <COButton text>
              <AddIcon />
              Add Hotel Stay
            </COButton>
          </div>
        </div>
      ),
      content: (
        <div className={styles.accordionContainer}>
          <div className={styles.accordionContentContainer}>
            {hotelFields.length === 0 ? (
              <div className={styles.noEntriesMessage}>
                No hotel stay entries. Click "Add Hotel Stay" to add one.
              </div>
            ) : (
              hotelFields.map((field, index) => {
                return (
                  <div key={field.id}>
                    <Category6Layout
                      index={index}
                      type="hotel"
                      remove={removeHotel}
                      setActiveTabKey={setActiveTabKey}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      ),
    },
  ];
};
